import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DeleteTrashRemove, Edit, ErrorWarningtraiangle } from '../../../assets/icons';
import {
  useDeletePriceGuardrailMutation,
  useFetchPriceGuardrailsQuery,
} from '../../../services/priceGuardrails.api';
import { PriceGuardrail } from '../../../stores/types/priceGuardrails.interface';
import {
  ButtonSize,
  ButtonType,
  ColorType,
  Icon,
  Label,
  LabelType,
  useGlobalModalContext,
} from '../../_ui';
import { Card } from '../../_ui/Card.component';
import { MODAL_TYPES } from '../../_ui/modal/GlobalModal.component';
import { EditGuardrailModal } from './EditGuardrailModal.component';

const columnHelper = createColumnHelper<PriceGuardrail>();

export const PriceGuardrails = () => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();
  const { data: guardrails = [], isLoading, isError } = useFetchPriceGuardrailsQuery();

  const conditionTypes = useMemo(() => {
    const types = new Set<string>();
    guardrails.forEach((g) => g.conditions.forEach((c) => types.add(c.guardrailType)));
    return Array.from(types);
  }, [guardrails]);

  const handleCreate = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('support_section.guardrail_create_title'),
      overflow: 'auto',
      width: '560px',
      height: 'fit-content',
      onRenderBody: () => <EditGuardrailModal />,
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleEdit = (guardrail: PriceGuardrail) => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('support_section.guardrail_edit_title'),
      overflow: 'auto',
      width: '560px',
      height: 'fit-content',
      onRenderBody: () => <EditGuardrailModal guardrail={guardrail} />,
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleDelete = (guardrail: PriceGuardrail) => {
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('support_section.guardrail_delete_title'),
      width: '400px',
      height: 'max-content',
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.NEGATIVE,
      message: (
        <Trans
          i18nKey='support_section.guardrail_delete_message'
          values={{ id: guardrail.id.slice(0, 8) }}
        />
      ),
      mutationHook: useDeletePriceGuardrailMutation,
      mutationParams: guardrail.id,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
        },
        {
          label: t('delete'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          isSubmit: true,
        },
      ],
      shouldCloseOnOverlayClick: false,
    });
  };

  const columns: ColumnDef<PriceGuardrail, any>[] = [
    ...conditionTypes.map((type) =>
      columnHelper.display({
        id: `condition_${type}`,
        header: type.replaceAll('_', ' '),
        cell: ({ row }) =>
          row.original.conditions.find((c) => c.guardrailType === type)?.matchValue ?? '',
        size: 180,
      }),
    ),
    columnHelper.accessor('restrictedPricingRules', {
      header: t('support_section.price_guardrails_restricted_rules'),
      cell: (info: { getValue: () => string[] }) => info.getValue().join(', '),
      size: 200,
    }),
    columnHelper.accessor('message', {
      header: t('support_section.price_guardrails_message'),
      cell: (info: { getValue: () => Record<string, string> }) => {
        return info.getValue().en!;
      },
      size: 300,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className='flex items-center gap-3'>
          <Icon
            src={Edit}
            color={ColorType.GREY5}
            className='cursor-pointer hover:opacity-70'
            onClick={() => handleEdit(row.original)}
            isFocusable
          />
          <Icon
            src={DeleteTrashRemove}
            color={ColorType.NEGATIVE}
            className='cursor-pointer hover:opacity-70'
            onClick={() => handleDelete(row.original)}
            isFocusable
          />
        </div>
      ),
      size: 80,
    }),
  ];

  const table = useReactTable({
    data: guardrails,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Label text={t('loading')} type={LabelType.BODY2} color={ColorType.GREY5} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Label
          text={t('support_section.price_guardrails_error')}
          type={LabelType.BODY2}
          color={ColorType.NEGATIVE}
        />
      </div>
    );
  }

  return (
    <div className='p-6'>
      <Card
        title={t('support_section.price_guardrails')}
        headerButtonLabel={t('support_section.guardrail_new')}
        headerButtonOnClick={handleCreate}
      >
        {guardrails.length === 0 ? (
          <div className='flex items-center justify-center py-20'>
            <Label
              text={t('support_section.price_guardrails_empty')}
              type={LabelType.BODY2}
              color={ColorType.GREY5}
            />
          </div>
        ) : (
          <div className='overflow-x-auto overflow-y-visible'>
            <table
              className='border-collapse'
              style={{ minWidth: '100%', width: table.getTotalSize() }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='bg-white'
                    style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minSize ?? 100,
                        }}
                        className='px-4 py-3 text-left text-sm font-semibold font-inter text-grey6'
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className='hover:bg-grey1'>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                        className='px-4 py-3 text-sm text-gray-900 overflow-hidden whitespace-nowrap'
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
